'use client';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { RemoteAvatar } from '@ps/ui/atoms/RemoteAvatar';
import { DialogTitle } from '@ps/ui/atoms/dialog';

import { CommunityModal } from '../../community-channels/components/common/CommunityModal';
import type { CommunityChannel } from '../../community-channels/types/CommunityChannel';
import {
  categoryTitles,
  useStoreCategories,
} from '../../subscribe/hooks/useStoreCategories';
import { channelSubscribeHref } from './channelSubscribeHref';

interface Props {
  /** The channel the viewer tried to join; `null` keeps the modal closed. */
  channel: CommunityChannel | null;
  creatorName: string;
  creatorAvatar?: string;
  bioContext: BioContext;
  /** Dismiss handler ("I don't want to join the fun!" / close). */
  onClose: () => void;
}

/**
 * The subscribe blocker a visitor sees when they tap "Join this Channel" without
 * the required subscription — the web port of PopshopRN's
 * `CommunityChannelBlockerView` (`_userAccessBlocker`, `join-channel` scene).
 * Creator card + "You need to subscribe to join the fun!" + promo banner, then
 * "Subscribe Now and Join Channel" (opens the subscribe picker floored at the
 * channel's required tier) or "I don't want to join the fun!" to dismiss.
 */
export function ChannelSubscribeBlockerModal({
  channel,
  creatorName,
  creatorAvatar,
  bioContext,
  onClose,
}: Props) {
  const router = useRouter();
  const categories = useStoreCategories(bioContext.creatorId);
  const subtitle = categoryTitles(categories);

  const onSubscribe = () => {
    if (!channel) return;
    const subscribeUrl = channelSubscribeHref(bioContext, channel);
    onClose();
    router.push(subscribeUrl);
  };

  return (
    <CommunityModal
      open={channel != null}
      onOpenChange={open => !open && onClose()}
      bodyClassName="gap-5"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
        <RemoteAvatar
          src={creatorAvatar}
          fallback={(creatorName || '?').charAt(0).toUpperCase()}
          alt={creatorName}
          size={48}
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-base font-semibold text-white">
            {creatorName}
          </span>
          {subtitle ? (
            <span className="truncate text-sm text-neutral-400">
              {subtitle}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <DialogTitle className="text-center text-xl font-bold text-white">
          You need to subscribe to join the fun!
        </DialogTitle>
        <p className="text-center text-sm text-neutral-300">
          This channel is private, so you&apos;ll need to subscribe to the
          creator to access this exclusive space and chat with them.
        </p>
      </div>

      <img
        src="/images/community/subscribe-banner.png"
        alt=""
        className="w-full rounded-lg"
        style={{ aspectRatio: '940 / 628' }}
      />

      <div className="flex flex-col gap-1 border-t border-neutral-700 pt-4">
        <button
          type="button"
          onClick={onSubscribe}
          className="bg-mint-green-300 hover:bg-mint-green-400 w-full rounded-lg px-4 py-3 text-sm font-bold text-black transition"
        >
          Subscribe Now and Join Channel
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2 text-sm font-medium text-neutral-400 transition hover:text-white"
        >
          I don&apos;t want to join the fun!
        </button>
      </div>
    </CommunityModal>
  );
}
